import axios from "axios";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import API_BASE_URL from "../config/api";

const BASE_URL = API_BASE_URL;

/** Create a new AAC Block test request */
export const createAacBlockTestRequest = async (testRequestData) => {
  const url = `${BASE_URL}/aac-block/test-requests`;
  try {
    const response = await axios.post(url, testRequestData);
    return response.data;
  } catch (error) {
    console.error("Error creating AAC Block test request:", error);
    if (error.response?.status === 404) {
      alert(
        "⚠️ Backend Endpoint Not Found\n\n" +
          "The AAC Block API is not available. Ensure the backend is deployed and restarted.\n\n" +
          "Endpoint: " +
          url
      );
      const err = new Error("AAC Block API endpoint not found (404). Deploy backend and restart.");
      err.originalError = error;
      err.isDeploymentError = true;
      throw err;
    }
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to create AAC Block test request. Please try again.");
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

/** Save AAC Block test observations (form data + block images) */
export const saveAacBlockObservations = async (testRequestId, observationsData) => {
  const url = `${BASE_URL}/aac-block/test-observations/${testRequestId}`;
  try {
    const response = await axios.post(url, observationsData);
    return response.data;
  } catch (error) {
    console.error("Error saving AAC Block observations:", error);
    handleApiError(error, "Failed to save AAC Block observations. Please try again.");
    throw error;
  }
};

/** Get AAC Block test observations for a request */
export const getAacBlockObservations = async (testRequestId) => {
  try {
    const response = await axios.get(`${BASE_URL}/aac-block/test-observations/${testRequestId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching AAC Block observations:", error);
    handleApiError(error, "Failed to fetch AAC Block observations. Please try again.");
    throw error;
  }
};

/** Get all AAC Block test requests (for dashboard/list) */
export const getAllAacBlockTestRequests = async () => {
  const url = `${BASE_URL}/aac-block/test-requests`;
  try {
    const response = await axios.get(url);
    return response.data.test_requests || [];
  } catch (error) {
    console.error("Error fetching AAC Block test requests:", error);
    if (error.response?.status === 404) {
      console.warn("AAC Block endpoint not found (404). Ensure backend is deployed.");
    }
    return [];
  }
};

/** Get a single AAC Block test request by ID (with observation) */
export const getAacBlockTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/aac-block/test-requests/${testRequestId}`;
  try {
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("Error fetching AAC Block test request:", error);
    handleApiError(error, "Failed to fetch AAC Block test request. Please try again.");
    throw error;
  }
};

/** Update an AAC Block test request */
export const updateAacBlockTestRequest = async (testRequestId, testRequestData) => {
  const url = `${BASE_URL}/aac-block/test-requests/${testRequestId}`;
  try {
    const response = await axios.put(url, testRequestData);
    return response.data;
  } catch (error) {
    console.error("Error updating AAC Block test request:", error);
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to update AAC Block test request. Please try again.");
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

/** Delete an AAC Block test request */
export const deleteAacBlockTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/aac-block/test-requests/${testRequestId}`;
  try {
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    console.error("Error deleting AAC Block test request:", error);
    handleApiError(error, "Failed to delete AAC Block test request. Please try again.");
    throw error;
  }
};
